"use client"

/**
 * Focus Indicator Component
 * Provides a visual focus indicator for keyboard navigation
 */

import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"

export function FocusIndicator() {
  const [focusRect, setFocusRect] = useState<DOMRect | null>(null)
  const [isKeyboard, setIsKeyboard] = useState(false)

  useEffect(() => {
    let lastInputWasKeyboard = false

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        lastInputWasKeyboard = true
        setIsKeyboard(true)
      }
    }

    const handleMouseDown = () => {
      lastInputWasKeyboard = false
      setIsKeyboard(false)
      setFocusRect(null)
    }

    const handleFocus = (e: FocusEvent) => {
      if (!lastInputWasKeyboard) return

      const target = e.target as HTMLElement
      if (target && typeof target.getBoundingClientRect === "function") {
        const rect = target.getBoundingClientRect()
        setFocusRect(rect)
      }
    }

    const handleBlur = () => {
      setFocusRect(null)
    }

    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("mousedown", handleMouseDown)
    window.addEventListener("focusin", handleFocus, true)
    window.addEventListener("focusout", handleBlur, true)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("mousedown", handleMouseDown)
      window.removeEventListener("focusin", handleFocus, true)
      window.removeEventListener("focusout", handleBlur, true)
    }
  }, [])

  if (!isKeyboard || !focusRect) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="pointer-events-none fixed z-[9999] rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-background"
        style={{
          left: focusRect.left - 2,
          top: focusRect.top - 2,
          width: focusRect.width + 4,
          height: focusRect.height + 4,
        }}
      />
    </AnimatePresence>
  )
}
